"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft } from "lucide-react";
import { AuthProvider, useAuth } from "@/contexts/auth-context";
import { getDashboardUrl } from "@/lib/dashboard-url";
import { Sidebar } from "@/components/sidebar";

function AdminLayoutContent({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [isInIframe, setIsInIframe] = useState(false);

  // Detect if running in iframe
  useEffect(() => {
    setIsInIframe(window.self !== window.top);
  }, []);

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  // If not authenticated, AuthProvider will handle redirect
  if (!isAuthenticated) {
    return null;
  }

  const handleBackToDashboard = () => {
    const dashboardUrl = getDashboardUrl();
    window.location.href = dashboardUrl;
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar - Always shown */}
      <Sidebar />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - Only shown when NOT in iframe */}
        {!isInIframe && (
          <header className="h-14 bg-background border-b flex-shrink-0">
            <div className="flex items-center h-full px-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToDashboard}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">Voltar ao Dashboard</span>
              </Button>
            </div>
          </header>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-background">{children}</main>
      </div>
    </div>
  );
}

export default function AdminLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  return (
    <AuthProvider>
      <AdminLayoutContent>{children}</AdminLayoutContent>
    </AuthProvider>
  );
}
